import { toast } from '@warp-ds/elements/toast';
import React, { useEffect } from 'react';
import { getHuckConfig } from '../utils/getHuckConfig.js';

type Config = {
  type: 'success' | 'warning' | 'error';
  message: string;
  showForMs?: number;
};

type ToastContext = {
  showToast: (config: Config) => void;
};

const context = React.createContext<ToastContext | undefined>(undefined);

export const ToastProvider = ({ children }: React.PropsWithChildren) => {
  const config = getHuckConfig();
  const [alert, setAlert] = React.useState<Config | null>(null);
  const activeToastsRef = React.useRef<Set<string>>(new Set());

  const showToast = (toastConfig: Config) => {
    const toastKey = `${toastConfig.type}-${toastConfig.message}`;

    // Don't show duplicate toasts
    if (activeToastsRef.current.has(toastKey)) {
      return;
    }

    setAlert(toastConfig);
  };

  useEffect(() => {
    if (alert && !config.development) {
      const toastKey = `${alert.type}-${alert.message}`;
      const duration = alert.showForMs || 5000;

      // Add to active toasts
      activeToastsRef.current.add(toastKey);

      toast(alert.message, { duration, type: alert.type });

      // Remove from active toasts after duration
      setTimeout(() => {
        activeToastsRef.current.delete(toastKey);
      }, duration);
    }
  }, [alert]);

  return <context.Provider value={{ showToast }}>{children}</context.Provider>;
};

export const useToast = () => {
  const contextValue = React.useContext(context);
  if (!contextValue) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return contextValue;
};
